/**
 * MuxSignal fleet tracking catalog — single source of truth.
 *
 * Four plans only. Every plan is a per-vehicle monthly SaaS subscription.
 * Hardware is furnished as part of the subscription unless it is explicitly
 * sold or leased on an order form.
 *
 * Two published rate cards plus an admin-only custom rate:
 *  - standard : public list rate shown on the website
 *  - apple    : Apple partner-program rate (partner portal / calculator)
 *  - custom   : admin-entered monthly amount for negotiated agreements
 */

export type PriceMode = "standard" | "apple" | "custom";

export const PRICE_MODES: { value: PriceMode; label: string; description: string }[] = [
  { value: "standard", label: "Standard", description: "Public list rate" },
  { value: "apple", label: "Apple Partner", description: "Apple leasing program rate" },
  { value: "custom", label: "Custom", description: "Negotiated monthly rate" },
];

/** Contract terms MuxSignal sells. Billing is always monthly. */
export const TERM_OPTIONS = [12, 24, 36, 48, 60] as const;
export type TermMonths = (typeof TERM_OPTIONS)[number];
export const DEFAULT_TERM: TermMonths = 36;

/** Hardware credit: $100 per vehicle on paid-up-front hardware, 36+ month terms. */
export const HARDWARE_CREDIT_PER_UNIT = 100;
export const HARDWARE_CREDIT_MIN_TERM = 36;

export interface FleetPlan {
  /** Paddle product external_id and Apple pricing coverage_key share this key. */
  key: "fleet_basic" | "fleet_obd" | "fleet_obd_backup" | "fleet_camera";
  /** coverage_key used by the Apple leasing pricing table. */
  coverageKey: "basic" | "obd" | "obd_backup" | "camera";
  name: string;
  tagline: string;
  /** Paddle price external_ids. */
  priceIds: { standard: string; apple: string };
  /** Published monthly rates per vehicle, in dollars. */
  rates: { standard: number; apple: number };
  features: string[];
  popular?: boolean;
}

export const FLEET_PLANS: FleetPlan[] = [
  {
    key: "fleet_basic",
    coverageKey: "basic",
    name: "Fleet Basic Tracking",
    tagline: "Hardwired GPS location, trips and reporting",
    priceIds: { standard: "fleet_basic_standard", apple: "fleet_basic_apple" },
    rates: { standard: 19.99, apple: 16.99 },
    features: [
      "Real-time GPS location and trip history",
      "Geofencing with arrival and departure alerts",
      "Speeding and after-hours movement alerts",
      "Mobile and web dashboard",
      "Standard reporting exports",
    ],
  },
  {
    key: "fleet_obd",
    coverageKey: "obd",
    name: "Fleet OBD Tracking",
    tagline: "Plug-and-play tracking with engine diagnostics",
    priceIds: { standard: "fleet_obd_standard", apple: "fleet_obd_apple" },
    rates: { standard: 29.99, apple: 24.99 },
    features: [
      "Everything in Fleet Basic Tracking",
      "Plug-and-play OBD-II installation",
      "Engine fault codes and diagnostics",
      "Driver behavior scoring",
      "Fuel and maintenance reporting",
    ],
    popular: true,
  },
  {
    key: "fleet_obd_backup",
    coverageKey: "obd_backup",
    name: "Fleet OBD with Backup Tracking",
    tagline: "OBD tracking plus an independent backup tracker",
    priceIds: { standard: "fleet_obd_backup_standard", apple: "fleet_obd_backup_apple" },
    rates: { standard: 34.99, apple: 30.99 },
    features: [
      "Everything in Fleet OBD Tracking",
      "Secondary battery-powered backup tracker",
      "Theft recovery coverage if the primary unit is removed",
      "Tamper and disconnect alerts",
      "Priority support queue",
    ],
  },
  {
    key: "fleet_camera",
    coverageKey: "camera",
    name: "Fleet Camera Tracking",
    tagline: "Tracking with AI dash camera and video evidence",
    priceIds: { standard: "fleet_camera_standard", apple: "fleet_camera_apple" },
    rates: { standard: 39.99, apple: 36.99 },
    features: [
      "Everything in Fleet OBD with Backup Tracking",
      "AI dash camera with road-facing video",
      "Harsh event video clips on demand",
      "Driver coaching workflow",
      "Video retention and cloud storage",
    ],
  },
];

/** Paddle product/price ids used for admin-entered custom monthly rates. */
export const CUSTOM_PLAN = {
  productId: "fleet_custom",
  placeholderPriceId: "fleet_custom_placeholder",
  name: "Fleet Custom Tracking",
} as const;

export function getPlan(key: string): FleetPlan | undefined {
  return FLEET_PLANS.find((p) => p.key === key || p.coverageKey === key);
}

/** Resolve the Paddle price external_id for a plan + published rate mode. */
export function getPriceId(planKey: string, mode: Exclude<PriceMode, "custom">): string | undefined {
  return getPlan(planKey)?.priceIds[mode];
}

/** Monthly rate per vehicle for a plan + published rate mode. */
export function getRate(planKey: string, mode: Exclude<PriceMode, "custom">): number | undefined {
  return getPlan(planKey)?.rates[mode];
}

/** Look up a plan by any Paddle price external_id, so stored rows can be labeled. */
export function planFromPriceId(priceId?: string | null): { plan?: FleetPlan; mode: PriceMode } {
  if (!priceId) return { mode: "standard" };
  if (priceId.startsWith(CUSTOM_PLAN.productId)) return { mode: "custom" };
  for (const plan of FLEET_PLANS) {
    if (plan.priceIds.standard === priceId) return { plan, mode: "standard" };
    if (plan.priceIds.apple === priceId) return { plan, mode: "apple" };
  }
  return { mode: "standard" };
}

export interface HardwareCreditInput {
  /** Contract term in months. */
  termMonths: number;
  /** Number of vehicles / devices on the agreement. */
  vehicles: number;
  /** Retail hardware charge per vehicle, before any credit. */
  hardwareRetailPerUnit: number;
  /** True when the customer is financing/leasing hardware instead of paying up front. */
  financedHardware: boolean;
}

export interface HardwareCreditResult {
  eligible: boolean;
  /** Credit applied per vehicle, capped at the hardware charge for that vehicle. */
  creditPerUnit: number;
  creditTotal: number;
  /** Hardware retail per vehicle after the credit. */
  netHardwarePerUnit: number;
  netHardwareTotal: number;
  /** Plain-English reason the credit does not apply, if it does not. */
  ineligibleReason?: string;
}

/**
 * The one hardware credit rule used everywhere: quoting, order forms,
 * the Apple calculator, admin forecasting and billing.
 *
 * $100 per vehicle, only on paid-up-front hardware and only on terms of
 * 36 months or longer, and never more than the hardware charge itself
 * (so it can never create a negative line or a refund).
 */
export function computeHardwareCredit(input: HardwareCreditInput): HardwareCreditResult {
  const vehicles = Math.max(0, Math.floor(input.vehicles || 0));
  const retailPerUnit = Math.max(0, input.hardwareRetailPerUnit || 0);

  let ineligibleReason: string | undefined;
  if (input.financedHardware) {
    ineligibleReason = "Hardware credit does not apply to leased or financed hardware.";
  } else if ((input.termMonths || 0) < HARDWARE_CREDIT_MIN_TERM) {
    ineligibleReason = `Hardware credit requires a ${HARDWARE_CREDIT_MIN_TERM}-month or longer term.`;
  } else if (retailPerUnit <= 0) {
    ineligibleReason = "No up-front hardware charge to credit against.";
  }

  const eligible = !ineligibleReason;
  const creditPerUnit = eligible ? Math.min(HARDWARE_CREDIT_PER_UNIT, retailPerUnit) : 0;

  return {
    eligible,
    creditPerUnit,
    creditTotal: creditPerUnit * vehicles,
    netHardwarePerUnit: Math.max(0, retailPerUnit - creditPerUnit),
    netHardwareTotal: Math.max(0, retailPerUnit - creditPerUnit) * vehicles,
    ineligibleReason,
  };
}

export const formatUsd = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
